import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { formatEnumLabel } from '../constants/enums';

/**
 * Dialog opened from 1st/2nd Approval — Approve or Decline with optional/required comment.
 * Used for deployment stages and monitoring reviews.
 */
export default function DeploymentStageApprovalDialog({
  open,
  onClose,
  onSubmit,
  stageStatus,
  slot,
  currentUser,
  saving = false,
  existingComment = '',
  title,
  description,
}) {
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setComment(existingComment || '');
      setError('');
    }
  }, [open, existingComment]);

  const slotLabel = slot === 1 ? '1st Approval' : '2nd Approval';
  const stageLabel = formatEnumLabel(stageStatus) || stageStatus || 'item';
  const dialogTitle = title || `${slotLabel} — ${stageLabel}`;
  const approver = [currentUser?.name, currentUser?.email].filter(Boolean).join(' · ') || 'Logged-in user';

  const handleDecision = async (decision) => {
    const trimmed = comment.trim();
    if (decision === 'DECLINED' && !trimmed) {
      setError('Please write a comment explaining the decline.');
      return;
    }
    setError('');
    await onSubmit?.({ decision, comment: trimmed });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Approver: {approver}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {description || (
            <>
              Approve this deployment stage, or decline it and leave a comment.
            </>
          )}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="Comment"
          placeholder={
            'Optional for Approve. Required for Decline (what must be fixed before approval).'
          }
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
            if (error) setError('');
          }}
          error={Boolean(error)}
          helperText={error || ' '}
          disabled={saving}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Box flex={1} />
        <Button
          color="error"
          variant="outlined"
          disabled={saving}
          onClick={() => handleDecision('DECLINED')}
          sx={{ textTransform: 'none' }}
        >
          Decline
        </Button>
        <Button
          color="secondary"
          variant="contained"
          disabled={saving}
          onClick={() => handleDecision('APPROVED')}
          sx={{ textTransform: 'none' }}
        >
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  );
}
